import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
	Radio,
	RadioGroup,
	FormControlLabel,
	TextField,
	Typography,
	Card,
	CardContent,
	Autocomplete,
	Grid,
	FormHelperText,
	Tooltip,
} from "../../../common/MaterialComponent";
import BatchMessageDetailModel from "../../../models/BatchMessage/BatchMessageDetailModel";
import Shared from "../../../common/shared";
import { useGetComponentStringsContext } from "../../../hooks/useGetComponentStrings";
import { BatchMessageTypeEnum } from "../../../common/enum";
import { GetAllAccessibleCountries } from "../../../api/countryController";
import CountryModel from "../../../models/Country/CountryModel";
import "./MessageTypeSelection.css";

export interface MessageTypeSelectionProps {
	batchNotification: BatchMessageDetailModel;
	onBatchNotificationChange: React.Dispatch<
		React.SetStateAction<BatchMessageDetailModel>
	>;
	setHasStepConfigChanged: React.Dispatch<React.SetStateAction<boolean>>;
	highlightRequiredDetail: boolean;
	isReadOnly: boolean;
}

const MessageTypeSelection: React.FC<MessageTypeSelectionProps> = ({
	batchNotification,
	onBatchNotificationChange,
	setHasStepConfigChanged,
	highlightRequiredDetail,
	isReadOnly,
}) => {
	const strings = useGetComponentStringsContext().MessageTypeSelection;
	const [countryList, setCountryList] = useState<CountryModel[]>([]);
	const [selectedCountry, setSelectedCountry] = useState<CountryModel | null>(
		null
	);
	const setSelectedCountryDetail = useCallback(
		(countryList: CountryModel[]) => {
			// set country as selected from country list if already saved
			const countryDetail = countryList.find(
				(c) => c.id === batchNotification?.country
			);
			if (countryDetail) {
				setSelectedCountry(countryDetail);
			}
			if (
				(!batchNotification.country && countryList.length === 1) ||
				(countryDetail?.africasTalkingEnabled &&
					batchNotification.batchMessageType === BatchMessageTypeEnum.Voice)
			) {
				onBatchNotificationChange((prevBatchMessageDetail) => {
					return {
						...prevBatchMessageDetail,
						// if only one country available for user then by default make it selected
						country: countryList[0].id as string,
						batchMessageType: countryDetail?.africasTalkingEnabled
							? BatchMessageTypeEnum.SMS
							: prevBatchMessageDetail.batchMessageType,
					};
				});
				setSelectedCountry(countryList[0]);
				setHasStepConfigChanged(true);
			}
		},
		[
			batchNotification.batchMessageType,
			batchNotification.country,
			onBatchNotificationChange,
			setHasStepConfigChanged,
		]
	);

	//use effect for getting country list
	//equivalent to component did mount
	useEffect(() => {
		async function getCountryList() {
			//only run if country list empty
			if (countryList.length <= 0) {
				try {
					const res = await GetAllAccessibleCountries();
					if (res.error) {
						Shared.showSnackbarMessage(strings.countryListErrorText);
					} else {
						setCountryList(res.responseObj ? res.responseObj : []);
						setSelectedCountryDetail(res.responseObj as CountryModel[]);
					}
				} catch (err) {
					Shared.showSnackbarMessage(strings.countryListErrorText);
				}
			}
		}
		getCountryList();
	}, [
		strings.countryListErrorText,
		countryList.length,
		setSelectedCountryDetail,
		batchNotification?.country,
	]);

	const isWhatsappBlockedCountry = useMemo(() => {
		return selectedCountry
			? Shared.whatsappBlockedCountries.includes(selectedCountry.id)
			: false;
	}, [selectedCountry]);

	const onMessageTitleChange = (event: any) => {
		if (isReadOnly) {
			return;
		}
		onBatchNotificationChange((prevBatchMessageDetail) => {
			return {
				...prevBatchMessageDetail,
				title: event.target.value,
			};
		});
		setHasStepConfigChanged(true);
	};

	const onMessageTypeChange = (event: any) => {
		if (isReadOnly) {
			return;
		}
		onBatchNotificationChange((prevBatchMessageDetail) => {
			return {
				...prevBatchMessageDetail,
				batchMessageType: event.target.value,
			};
		});
		setHasStepConfigChanged(true);
	};

	const onCountryValueChange = (event: any, value: CountryModel | null) => {
		if (isReadOnly) {
			return;
		}
		setSelectedCountry(value);
		onBatchNotificationChange((prevBatchMessageDetail) => {
			return {
				...prevBatchMessageDetail,
				country: value?.id as string,
				batchMessageType: value ? getAutoSelectBatchMessageType(value) : "",
			};
		});
		setHasStepConfigChanged(true);
	};

	const getAutoSelectBatchMessageType = (countryDetail: CountryModel) => {
		const isWhatsappDisabled: boolean =
			!countryDetail.isWhatsappEnabled ||
			(countryDetail.isWhatsappEnabled &&
				(Shared.whatsappBlockedCountries.includes(countryDetail.id) ||
					countryDetail.africasTalkingEnabled));
		const isVoiceDisabled: boolean =
			!countryDetail.isVoiceEnabled ||
			(countryDetail.isVoiceEnabled && countryDetail.africasTalkingEnabled);
		const isSMSDisabled: boolean = !countryDetail.isSMSEnabled;

		if (!isSMSDisabled && isVoiceDisabled && isWhatsappDisabled) {
			return BatchMessageTypeEnum.SMS;
		} else if (!isVoiceDisabled && isWhatsappDisabled && isSMSDisabled) {
			return BatchMessageTypeEnum.Voice;
		} else if (!isWhatsappDisabled && isVoiceDisabled && isSMSDisabled) {
			return BatchMessageTypeEnum.WhatsApp;
		} else {
			return "";
		}
	};

	const shouldFocus = (callingElement: "Country" | "Name") => {
		//only focus if highlightdetail is true
		if (highlightRequiredDetail === false) {
			return false;
		}
		if (
			batchNotification.country === null ||
			batchNotification.title === null
		) {
			//error
			return false;
		}
		//returns true if should focus element
		//focus country name first then name if both empty
		if (!batchNotification.country) {
			//returns true if country name calling function
			if (callingElement === "Country") {
				setHasStepConfigChanged(true);
				return true;
			}
		} else if (!batchNotification.title) {
			//returns true and should focus if calling component was Name
			if (callingElement === "Name") {
				setHasStepConfigChanged(true);
				return true;
			}
		} else {
			return false;
		}

		return false;
	};

	return (
		<>
			<div className="messageType" data-testid="messageTypeSelection">
				<Card variant="outlined">
					<CardContent>
						<Grid container spacing={1}>
							<Grid item xs={12} data-testid="messageTypeError">
								<RadioGroup
									row
									aria-label="messageType"
									name="messageType"
									value={batchNotification.batchMessageType}
									onChange={onMessageTypeChange}
								>
									<Tooltip
										title={
											selectedCountry &&
											(!selectedCountry.isVoiceEnabled ||
												selectedCountry?.africasTalkingEnabled)
												? `${strings.voiceLabel} connectivity is not supported in ${selectedCountry.id}`
												: ""
										}
										classes={{
											tooltip: "infoTooltip",
										}}
									>
										<FormControlLabel
											value={BatchMessageTypeEnum.Voice}
											control={<Radio color="primary" />}
											label={strings.voiceLabel}
											disabled={
												isReadOnly ||
												selectedCountry?.africasTalkingEnabled ||
												!selectedCountry?.isVoiceEnabled
											}
										/>
									</Tooltip>
									<Tooltip
										title={
											selectedCountry && !selectedCountry.isSMSEnabled
												? `${strings.smsLabel} connectivity is not supported in ${selectedCountry.id}`
												: ""
										}
										classes={{
											tooltip: "infoTooltip",
										}}
									>
										<FormControlLabel
											value={BatchMessageTypeEnum.SMS}
											control={<Radio color="primary" />}
											label={strings.smsLabel}
											disabled={isReadOnly || !selectedCountry?.isSMSEnabled}
										/>
									</Tooltip>

									<Tooltip
										title={
											selectedCountry &&
											(!selectedCountry.isWhatsappEnabled ||
												selectedCountry?.africasTalkingEnabled)
												? `${strings.whatsappLabel} connectivity is not supported in ${selectedCountry.id}`
												: ""
										}
										classes={{
											tooltip: "infoTooltip",
										}}
									>
										<FormControlLabel
											value={BatchMessageTypeEnum.WhatsApp}
											control={<Radio color="primary" />}
											label={strings.whatsappLabel}
											disabled={
												isReadOnly ||
												!selectedCountry?.isWhatsappEnabled ||
												isWhatsappBlockedCountry ||
												selectedCountry?.africasTalkingEnabled
											}
										/>
									</Tooltip>
								</RadioGroup>
								{highlightRequiredDetail &&
								!batchNotification.batchMessageType ? (
									<FormHelperText className="messageTypeError Mui-error">
										{selectedCountry &&
										!selectedCountry.isSMSEnabled &&
										(!selectedCountry.isVoiceEnabled ||
											(selectedCountry.isVoiceEnabled &&
												selectedCountry.africasTalkingEnabled)) &&
										(!selectedCountry.isWhatsappEnabled ||
											(selectedCountry.isWhatsappEnabled &&
												isWhatsappBlockedCountry) ||
											selectedCountry.africasTalkingEnabled)
											? strings.messageTypeNotConfiguredErrorText
											: strings.messageTypeErrorText}
									</FormHelperText>
								) : (
									<></>
								)}
							</Grid>
							<Grid item xs={6}>
								<Autocomplete
									data-testid="countryList"
									options={countryList}
									disableClearable={isReadOnly}
									onChange={onCountryValueChange}
									autoHighlight
									value={selectedCountry}
									getOptionLabel={(option) => option.id}
									getOptionSelected={(option, value) =>
										!value || option === value
									}
									getOptionDisabled={() => isReadOnly}
									renderInput={(params) => (
										<TextField
											{...params}
											helperText={
												highlightRequiredDetail && !batchNotification.country
													? strings.countryErrorText
													: ""
											}
											error={
												highlightRequiredDetail && !batchNotification.country
											}
											required
											label={strings.countryName}
											data-testid="countryNameInput"
											disabled={isReadOnly}
											inputRef={(input) =>
												input && shouldFocus("Country") && input.focus()
											}
										/>
									)}
								/>
							</Grid>
							<Grid item xs={6}>
								<Typography component="div">
									<TextField
										required
										id="standard-basic"
										label={strings.messageTitle}
										value={batchNotification.title}
										onChange={onMessageTitleChange}
										helperText={
											highlightRequiredDetail && !batchNotification.title
												? strings.messageTitleErrorText
												: ""
										}
										error={highlightRequiredDetail && !batchNotification.title}
										inputProps={{
											"data-testid": "messageTitle",
											readOnly: isReadOnly,
										}}
										fullWidth
										disabled={isReadOnly}
										inputRef={(input) =>
											input && shouldFocus("Name") && input.focus()
										}
									/>
								</Typography>
							</Grid>
						</Grid>
					</CardContent>
				</Card>
			</div>
		</>
	);
};
export default MessageTypeSelection;
